import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  AdminApiError,
  createNotification,
  createTrackingLink,
  exportContactsCsv,
  getAnalytics,
  getLeaderboard,
  getNotifications,
  getOverview,
  getSounds,
  getTrackingLinks,
  getTrialUsers,
  getUsers,
  loginWithPassword,
  setUserEnabled,
  updateSound,
  updateTrackingLink,
  type AdminUser,
  type Analytics,
  type ContactSegment,
  type ContactSort,
  type ContactSummary,
  type LeaderboardRow,
  type NotificationCampaign,
  type Overview,
  type PushDeliveryStatus,
  type SoundSetting,
  type TrackingAnalytics,
  type TrackingDestination,
  type TrackingPlatform,
  type TrialUser,
  type TrialUserSource,
  type TrialUserStatus,
} from "./api";

type Tab = "overview" | "users" | "contacts" | "tracking" | "analytics" | "leaders" | "notifications" | "sounds";
type AuthState = "checking" | "signed-in" | "login" | "forbidden";
type PanelLoad = { loading: boolean; error: string };
type IconName = "overview" | "users" | "contacts" | "tracking" | "analytics" | "leaders" | "notifications" | "sounds";
type PageMeta = { total: number; page: number; pageSize: number; totalPages: number };

const navigation: Array<{ label: string; items: Array<[Tab, string]> }> = [
  {
    label: "Workspace",
    items: [
      ["overview", "Overview"],
      ["users", "Players"],
      ["contacts", "Contacts"],
      ["tracking", "Campaign Links"],
      ["analytics", "Analytics"],
      ["leaders", "Leaderboards"],
    ],
  },
  {
    label: "Operations",
    items: [
      ["notifications", "Push"],
      ["sounds", "Game Settings"],
    ],
  },
];

const tabMeta: Record<Tab, { icon: IconName; eyebrow: string; title: string; copy: string }> = {
  overview: {
    icon: "overview",
    eyebrow: "Workspace",
    title: "Overview",
    copy: "Player growth, run outcomes, progression, and push reach in one operational view.",
  },
  users: {
    icon: "users",
    eyebrow: "Workspace",
    title: "Players",
    copy: "Find players and inspect progression, activity, economy, and push coverage.",
  },
  contacts: {
    icon: "contacts",
    eyebrow: "Audience",
    title: "Contacts",
    copy: "See every launch-list signup and browser-trial request, with confirmation and acquisition history.",
  },
  tracking: {
    icon: "tracking",
    eyebrow: "Acquisition",
    title: "Campaign Links",
    copy: "Measure which YouTube, TikTok, and Instagram posts turn visits into browser trials.",
  },
  analytics: {
    icon: "analytics",
    eyebrow: "Workspace",
    title: "Analytics",
    copy: "Review event volume, area activity, client versions, and platform mix.",
  },
  leaders: {
    icon: "leaders",
    eyebrow: "Competition",
    title: "Leaderboards",
    copy: "Audit global, map, and area rankings across every competitive metric.",
  },
  notifications: {
    icon: "notifications",
    eyebrow: "Messaging",
    title: "Push",
    copy: "Prepare player messages and review campaign status, audience, and history.",
  },
  sounds: {
    icon: "sounds",
    eyebrow: "Game settings",
    title: "Game Settings",
    copy: "Preview, tune, and enable live sound cues without redeploying the game.",
  },
};

const initialPanelLoads = (): Record<Tab, PanelLoad> => ({
  overview: { loading: true, error: "" },
  users: { loading: true, error: "" },
  contacts: { loading: true, error: "" },
  tracking: { loading: true, error: "" },
  analytics: { loading: true, error: "" },
  leaders: { loading: true, error: "" },
  notifications: { loading: true, error: "" },
  sounds: { loading: true, error: "" },
});

const metricLabels: Record<string, string> = {
  score: "Score",
  xp: "XP",
  cash: "Cash",
  kills: "Kills",
  fastest_clear: "Fastest clear",
  health_retained: "Health retained",
};

const number = (value: unknown) => Number(value ?? 0).toLocaleString();
const minutes = (value: unknown) => {
  const total = Math.floor(Number(value ?? 0) / 60_000);
  if (total < 60) return `${total}m`;
  return `${Math.floor(total / 60)}h ${total % 60}m`;
};
const percent = (part: number, total: number) => total ? `${Math.round((part / total) * 100)}%` : "0%";
const gameOrigin = (import.meta.env.VITE_AFTERRAIN_GAME_URL ?? "https://play.theafterrain.com").replace(/\/$/, "");
const soundUrl = (path: string) => path.startsWith("http") ? path : `${gameOrigin}${path}`;
const slugify = (value: string) => value
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .slice(0, 64);

const formatLeaderboardValue = (row: LeaderboardRow) => {
  if (row.formatted_value) return row.formatted_value;
  const value = Number(row.value ?? 0);
  if (row.metric === "fastest_clear") {
    const milliseconds = Math.abs(value);
    const wholeSeconds = Math.floor(milliseconds / 1000);
    const minutesPart = Math.floor(wholeSeconds / 60);
    const secondsPart = wholeSeconds % 60;
    return `${minutesPart}:${String(secondsPart).padStart(2, "0")}`;
  }
  if (row.metric === "cash") return `$${number(value)}`;
  return number(value);
};
const formatDateTime = (value: string | null | undefined) => value ? new Date(value).toLocaleString() : "—";

function Icon({ name }: { name: IconName }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
      {name === "overview" ? (
        <>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </>
      ) : null}
      {name === "users" ? (
        <>
          <circle cx="9" cy="8" r="3" />
          <path d="M3.5 19c.5-4 2.5-6 5.5-6s5 2 5.5 6" />
          <path d="M15 5.5a3 3 0 0 1 0 5.5M16 13c2.7.4 4.2 2.4 4.5 5" />
        </>
      ) : null}
      {name === "contacts" ? (
        <>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m4 7 8 6 8-6" />
        </>
      ) : null}
      {name === "tracking" ? (
        <>
          <path d="M8.5 15.5 15.5 8.5" />
          <path d="M10 7h7v7" />
          <path d="M19 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
        </>
      ) : null}
      {name === "analytics" ? (
        <>
          <path d="M4 19V9M10 19V5M16 19v-7M22 19V3" />
          <path d="M2 19h21" />
        </>
      ) : null}
      {name === "leaders" ? (
        <>
          <path d="M8 4h8v4a4 4 0 0 1-8 0V4Z" />
          <path d="M8 6H4v1a4 4 0 0 0 4 4M16 6h4v1a4 4 0 0 1-4 4M12 12v5M8 21h8M9 17h6" />
        </>
      ) : null}
      {name === "notifications" ? (
        <>
          <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" />
          <path d="M10 21h4" />
        </>
      ) : null}
      {name === "sounds" ? (
        <>
          <path d="M5 9v6h4l5 4V5L9 9H5Z" />
          <path d="M17 9a4 4 0 0 1 0 6M19 6a8 8 0 0 1 0 12" />
        </>
      ) : null}
    </svg>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <article className="stat">
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <small>{hint}</small> : null}
    </article>
  );
}

function PanelTitle({ title, meta }: { title: string; meta?: string }) {
  return (
    <div className="section-title">
      <h2>{title}</h2>
      {meta ? <span>{meta}</span> : null}
    </div>
  );
}

function Empty({ title, message, compact = false }: { title: string; message: string; compact?: boolean }) {
  return (
    <div className={compact ? "state-card compact" : "state-card"}>
      <span className="state-icon" aria-hidden="true">—</span>
      <strong>{title}</strong>
      <p>{message}</p>
    </div>
  );
}

function LoadingState({ message = "Loading data…" }: { message?: string }) {
  return (
    <div className="state-card loading-state" role="status">
      <div className="skeleton-block short" />
      <div className="skeleton-block" />
      <div className="skeleton-block" />
      <span className="sr-only">{message}</span>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="state-card error-state" role="alert">
      <span className="state-icon" aria-hidden="true">!</span>
      <strong>Data could not be loaded</strong>
      <p>{message}</p>
      <button className="secondary-button" onClick={onRetry}>Try again</button>
    </div>
  );
}

function PanelFeedback({ load, hasData, onRetry }: {
  load: PanelLoad;
  hasData: boolean;
  onRetry: () => void;
}) {
  if (load.error && hasData) {
    return (
      <div className="inline-alert" role="alert">
        <span>{load.error}</span>
        <button onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (load.loading && hasData) {
    return <div className="inline-status" role="status">Updating data…</div>;
  }
  return null;
}

function Pagination({ meta, onPage }: { meta: PageMeta; onPage: (page: number) => void }) {
  if (!meta.total) return null;
  const start = (meta.page - 1) * meta.pageSize + 1;
  const end = Math.min(meta.page * meta.pageSize, meta.total);
  return (
    <footer className="pagination" aria-label="Pagination">
      <span>{number(start)}–{number(end)} of {number(meta.total)}</span>
      <div>
        <button
          className="secondary-button"
          disabled={meta.page <= 1}
          onClick={() => onPage(meta.page - 1)}
        >
          Previous
        </button>
        <span>Page {meta.page} of {Math.max(1, meta.totalPages)}</span>
        <button
          className="secondary-button"
          disabled={meta.page >= meta.totalPages}
          onClick={() => onPage(meta.page + 1)}
        >
          Next
        </button>
      </div>
    </footer>
  );
}

function LoginScreen({ mode, message }: { mode: "login" | "forbidden"; message?: string | undefined }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("Checking owner credentials…");
    try {
      await loginWithPassword(username, password);
      setStatus("Owner session confirmed.");
      window.location.reload();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Admin sign-in failed.");
      setSubmitting(false);
    }
  }

  return (
    <main className="login-main">
      <section className="login-card" aria-labelledby="admin-login-title">
        <div className="login-brand">
          <div className="brand-mark">AR</div>
          <div><strong>Afterrain</strong><span>Admin</span></div>
        </div>
        <p className="kicker">Owner access</p>
        <h1 id="admin-login-title">{mode === "forbidden" ? "Access required" : "Sign in to admin"}</h1>
        <p className="login-copy">
          {mode === "forbidden"
            ? "This account is signed in but does not have owner permissions."
            : "Use your separate owner credentials. Player accounts and gamer tags do not provide admin access."}
        </p>
        {message ? <div className="login-warning">{message}</div> : null}
        <form className="credential-form" onSubmit={submit}>
          <label htmlFor="admin-username">Username
            <input
              id="admin-username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="AFTERRAIN"
              required
            />
          </label>
          <label htmlFor="admin-password">Password
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <button className="primary-button" disabled={submitting}>{submitting ? "Signing in…" : "Sign in"}</button>
          <span className="form-status" role="status">{status}</span>
        </form>
        <a className="login-foot" href="https://play.theafterrain.com">Back to the live game</a>
      </section>
    </main>
  );
}

function OverviewPanel({ overview, load, onRetry }: {
  overview: Overview | null;
  load: PanelLoad;
  onRetry: () => void;
}) {
  if (!overview && load.loading) return <LoadingState message="Loading overview…" />;
  if (!overview && load.error) return <ErrorState message={load.error} onRetry={onRetry} />;
  if (!overview) return <Empty title="No overview data" message="Overview metrics will appear when the API returns data." />;

  return (
    <>
      <PanelFeedback load={load} hasData onRetry={onRetry} />
      <section className="panel-grid overview-grid" aria-label="Overview metrics">
        <Stat label="Players" value={number(overview.totals.players)} hint={`${number(overview.totals.new_players_24h)} joined in 24h`} />
        <Stat label="30-day clears" value={number(overview.attempts.clears)} hint={`${percent(overview.attempts.clears, overview.attempts.attempts)} clear rate`} />
        <Stat label="30-day stops" value={number(overview.attempts.deaths)} hint={`${number(overview.attempts.attempts)} total attempts`} />
        <Stat label="Push devices" value={number(overview.push.enabled_devices)} hint={`${number(overview.push.ios_devices)} iOS · ${number(overview.push.android_devices)} Android`} />
        <Stat label="Total XP" value={number(overview.totals.total_xp)} />
        <Stat label="Total cash" value={`$${number(overview.totals.total_cash)}`} />
        <Stat label="Total kills" value={number(overview.totals.total_kills)} />
        <Stat label="Play time" value={minutes(overview.totals.total_play_time_ms)} />

        <section className="panel span-2">
          <PanelTitle title="Recent runs" meta="Latest 10" />
          {overview.recentAttempts.length ? (
            <div className="table-scroll">
              <table className="data-table compact-table">
                <thead><tr><th>Player</th><th>Area</th><th>Result</th><th>XP</th><th>Cash</th></tr></thead>
                <tbody>{overview.recentAttempts.map((run) => (
                  <tr key={String(run.id)}>
                    <td><strong>{String(run.gamer_tag ?? "Unknown")}</strong></td>
                    <td>{String(run.area_id ?? "—")}</td>
                    <td><span className={run.completed ? "pill good" : "pill warn"}>{run.completed ? "Clear" : "Stopped"}</span></td>
                    <td>{number(run.xp)}</td>
                    <td>${number(run.cash)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <Empty compact title="No recent runs" message="Completed and stopped runs will appear here." />}
        </section>

        <section className="panel span-2">
          <PanelTitle title="Area outcomes" meta="Last 30 days" />
          {overview.dropoffs.length ? (
            <div className="table-scroll">
              <table className="data-table compact-table">
                <thead><tr><th>Area</th><th>Attempts</th><th>Clears</th><th>Stops</th><th>Clear rate</th></tr></thead>
                <tbody>{overview.dropoffs.map((row) => (
                  <tr key={String(row.area_id)}>
                    <td><strong>{String(row.area_name)}</strong></td>
                    <td>{number(row.attempts)}</td>
                    <td>{number(row.clears)}</td>
                    <td>{number(row.deaths)}</td>
                    <td>{percent(Number(row.clears), Number(row.attempts))}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <Empty compact title="No area activity" message="Area outcomes will appear after players start runs." />}
        </section>
      </section>
    </>
  );
}

function RegisteredUsersView({ users, load, pageMeta, onLoad }: {
  users: AdminUser[];
  load: PanelLoad;
  pageMeta: PageMeta;
  onLoad: (value: string, page: number) => void;
}) {
  const [query, setQuery] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState("");

  async function changeAccess(user: AdminUser) {
    const enabled = Boolean(user.disabled_at);
    const action = enabled ? "restore" : "suspend";
    if (!window.confirm(`${action === "suspend" ? "Suspend" : "Restore"} ${user.gamer_tag}?`)) return;
    setUpdatingUserId(user.id);
    setActionStatus(`${action === "suspend" ? "Suspending" : "Restoring"} ${user.gamer_tag}…`);
    try {
      await setUserEnabled(user.id, enabled);
      setActionStatus(`${user.gamer_tag} is now ${enabled ? "active" : "suspended"}.`);
      onLoad(query, pageMeta.page);
    } catch (error) {
      setActionStatus(error instanceof Error ? error.message : "The account could not be updated.");
    } finally {
      setUpdatingUserId(null);
    }
  }

  return (
    <section className="panel table-panel">
      <div className="table-toolbar">
        <label className="search">Search players
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              onLoad(event.target.value, 1);
            }}
            placeholder="Gamer tag, name, or email"
          />
        </label>
        <span className="result-count">{load.loading ? "Updating…" : `${number(pageMeta.total)} registered`}</span>
      </div>
      {actionStatus ? <div className="inline-status" role="status">{actionStatus}</div> : null}
      {load.error && !users.length ? <ErrorState message={load.error} onRetry={() => onLoad(query, pageMeta.page)} /> : null}
      {load.loading && !users.length ? <LoadingState message="Loading players…" /> : null}
      <PanelFeedback load={load} hasData={users.length > 0} onRetry={() => onLoad(query, pageMeta.page)} />
      {!load.loading && !load.error && !users.length ? (
        <Empty title={query ? "No matching players" : "No players yet"} message={query ? "Try a different gamer tag, name, or email." : "New players will appear here after creating a game profile."} />
      ) : null}
      {users.length ? (
        <>
          <div className="table-scroll">
            <table className="data-table users-table">
              <thead><tr><th>Gamer tag</th><th>Role</th><th>Account</th><th>Email</th><th>Next area</th><th>XP</th><th>Cash</th><th>Kills</th><th>Play time</th><th>Push</th><th>Control</th></tr></thead>
              <tbody>{users.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.gamer_tag}</strong><small>{user.display_name ?? user.avatar_key}</small></td>
                  <td><span className={`pill ${user.role === "owner" ? "good" : "neutral"}`}>{user.role}</span></td>
                  <td><span className={`pill ${user.disabled_at ? "danger" : "good"}`}>{user.disabled_at ? "Suspended" : "Active"}</span></td>
                  <td>{user.primary_email ?? "Private"}</td>
                  <td><strong>{user.next_area_id ?? user.highest_area_id}</strong>{user.last_completed_area_id != null ? <small>Last cleared {user.last_completed_area_id}</small> : null}</td>
                  <td>{number(user.xp)}</td>
                  <td>${number(user.cash)}</td>
                  <td>{number(user.kills)}</td>
                  <td>{minutes(user.play_time_ms)}</td>
                  <td>{number(user.push_devices)}</td>
                  <td>{user.role === "owner" ? "Protected" : (
                    <button
                      type="button"
                      className={`status-toggle ${user.disabled_at ? "" : "active"}`}
                      disabled={updatingUserId === user.id}
                      onClick={() => void changeAccess(user)}
                    >
                      {updatingUserId === user.id ? "Updating" : user.disabled_at ? "Restore" : "Suspend"}
                    </button>
                  )}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <Pagination meta={pageMeta} onPage={(page) => onLoad(query, page)} />
        </>
      ) : null}
    </section>
  );
}

type ContactLoadInput = {
  search: string;
  page: number;
  status: TrialUserStatus | "";
  source: TrialUserSource | "";
  segment: ContactSegment | "";
  sort: ContactSort;
};

type ContactAudience = "" | ContactSegment | "app" | "blog";

function ContactsPanel({ users, load, pageMeta, summary, onLoad }: {
  users: TrialUser[];
  load: PanelLoad;
  pageMeta: PageMeta;
  summary: ContactSummary;
  onLoad: (input: ContactLoadInput) => void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<TrialUserStatus | "">("");
  const [audience, setAudience] = useState<ContactAudience>("");
  const [sort, setSort] = useState<ContactSort>("newest");
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const filters = (
    nextQuery = query,
    nextStatus = status,
    nextAudience = audience,
    nextSort = sort,
  ): Omit<ContactLoadInput, "page"> => ({
    search: nextQuery,
    status: nextStatus,
    segment: nextAudience === "launch" || nextAudience === "browser_trial" ? nextAudience : "",
    source: nextAudience === "app" || nextAudience === "blog" ? nextAudience : "",
    sort: nextSort,
  });
  const fetchPage = (
    page: number,
    nextQuery = query,
    nextStatus = status,
    nextAudience = audience,
    nextSort = sort,
  ) => {
    onLoad({ ...filters(nextQuery, nextStatus, nextAudience, nextSort), page });
  };

  async function exportCsv() {
    setExporting(true);
    setExportStatus("Preparing contact export…");
    try {
      const blob = await exportContactsCsv(filters());
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `afterrain-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setExportStatus("Contact CSV downloaded.");
    } catch (error) {
      setExportStatus(error instanceof Error ? error.message : "The contact export could not be created.");
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className="contacts-layout">
      <section className="contact-health" aria-label="Contact totals">
        <div><span>All contacts</span><strong>{number(summary.total)}</strong></div>
        <div><span>Launch list</span><strong>{number(summary.launch_contacts)}</strong></div>
        <div><span>Confirmed emails</span><strong>{number(summary.verified_emails)}</strong></div>
        <div><span>Delivered emails</span><strong>{number(summary.delivered_emails)}</strong></div>
        <div><span>Opened emails</span><strong>{number(summary.opened_emails)}</strong></div>
        <div><span>Browser trials</span><strong>{number(summary.trial_starts)}</strong></div>
        <div><span>Added in 7 days</span><strong>{number(summary.new_contacts_7d)}</strong></div>
      </section>
      <section className="contact-scope-note" aria-label="Email audience status">
        <div>
          <strong>Launch audience</strong>
          <span>Confirmed contacts can receive the promised launch and beta updates. Newsletter campaigns are not enabled yet.</span>
        </div>
        <div className="audience-pills">
          {summary.delivery_failures ? <span className="pill danger">{number(summary.delivery_failures)} delivery failures</span> : null}
          {summary.unsubscribed ? <span className="pill warn">{number(summary.unsubscribed)} unsubscribed</span> : <span className="pill neutral">No bulk sends</span>}
        </div>
      </section>
      <section className="panel table-panel">
        <div className="table-toolbar contacts-toolbar">
        <label className="search">Search contacts
          <input
            value={query}
            onChange={(event) => {
              const value = event.target.value;
              setQuery(value);
              fetchPage(1, value);
            }}
            placeholder="Name, email, campaign, or content"
          />
        </label>
        <label>Audience
          <select
            value={audience}
            onChange={(event) => {
              const value = event.target.value as ContactAudience;
              setAudience(value);
              fetchPage(1, query, status, value);
            }}
          >
            <option value="">All contacts</option>
            <option value="launch">Launch list</option>
            <option value="browser_trial">Browser trial requests</option>
            <option value="app">App contacts</option>
            <option value="blog">Blog contacts</option>
          </select>
        </label>
        <label>Confirmation
          <select
            value={status}
            onChange={(event) => {
              const value = event.target.value as TrialUserStatus | "";
              setStatus(value);
              fetchPage(1, query, value, audience);
            }}
          >
            <option value="">All confirmation states</option>
            <option value="pending">Pending delivery</option>
            <option value="emailed">Awaiting confirmation</option>
            <option value="failed">Delivery failed</option>
            <option value="verified">Confirmed</option>
            <option value="trial_started">Trial started</option>
          </select>
        </label>
        <label>Sort
          <select
            value={sort}
            onChange={(event) => {
              const value = event.target.value as ContactSort;
              setSort(value);
              fetchPage(1, query, status, audience, value);
            }}
          >
            <option value="newest">Newest first</option>
            <option value="recent_activity">Recent activity</option>
            <option value="name">Name A–Z</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
        <button className="secondary-button export-button" disabled={exporting} onClick={() => void exportCsv()}>
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
        <span className="result-count">{load.loading ? "Updating…" : `${number(pageMeta.total)} contacts`}</span>
      </div>
      {exportStatus ? <div className="inline-status" role="status">{exportStatus}</div> : null}
      {load.error && !users.length ? <ErrorState message={load.error} onRetry={() => fetchPage(pageMeta.page)} /> : null}
      {load.loading && !users.length ? <LoadingState message="Loading contacts…" /> : null}
      <PanelFeedback load={load} hasData={users.length > 0} onRetry={() => fetchPage(pageMeta.page)} />
      {!load.loading && !load.error && !users.length ? (
        <Empty
          title={query || status || audience ? "No matching contacts" : "No contacts yet"}
          message={query || status || audience ? "Adjust the search or filters to broaden this view." : "Website launch signups and browser access requests will appear here."}
        />
      ) : null}
      {users.length ? (
        <>
          <div className="table-scroll">
            <table className="data-table contacts-table">
              <thead>
                <tr>
                  <th>Contact</th><th>Audience</th><th>Confirmation</th><th>Acquisition</th><th>Browser trial</th><th>Interest</th><th>Signed up</th><th>Last activity</th>
                </tr>
              </thead>
              <tbody>{users.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.name || user.email}</strong><small>{user.name ? user.email : user.email_normalized}</small></td>
                  <td>
                    <div className="audience-pills">
                      {user.launch_updates_requested_at ? <span className="pill good">Launch list</span> : null}
                      {user.source === "play_gate" ? <span className="pill neutral">Trial request</span> : null}
                      {!user.launch_updates_requested_at && user.source !== "play_gate" ? <span className="pill neutral">{user.source}</span> : null}
                    </div>
                    <small>{user.launch_updates_consent_version || user.source.replaceAll("_", " ")}</small>
                  </td>
                  <td>
                    <span className={`pill ${user.email_updates_unsubscribed_at || user.funnel_status === "failed" ? "danger" : user.email_verified_at ? "good" : "neutral"}`}>
                      {user.email_updates_unsubscribed_at ? "Unsubscribed" : user.funnel_status === "failed" ? "Delivery failed" : user.email_verified_at ? "Confirmed" : "Awaiting"}
                    </span>
                    <small>
                      {user.email_verified_at
                        ? `Confirmed ${formatDateTime(user.email_verified_at)}`
                        : user.email_opened_at
                          ? `Opened ${formatDateTime(user.email_opened_at)}`
                          : user.email_delivered_at
                            ? `Delivered ${formatDateTime(user.email_delivered_at)}`
                            : user.email_failed_at || user.email_complained_at
                              ? `${user.email_last_event_type || "Failed"} ${formatDateTime(user.email_failed_at || user.email_complained_at)}`
                              : user.email_delivery_status || "Not sent"}
                    </small>
                  </td>
                  <td>
                    <strong>{user.tracking_content_title || user.tracking_slug || "Direct website"}</strong>
                    <small>{[user.tracking_platform, user.tracking_campaign_name].filter(Boolean).join(" · ") || user.source.replaceAll("_", " ")}</small>
                    {user.tracking_video_url ? <a className="video-link" href={user.tracking_video_url} target="_blank" rel="noreferrer">Open video</a> : null}
                  </td>
                  <td><strong>{user.trial_started_at ? "Started" : "Not started"}</strong><small>{formatDateTime(user.trial_started_at)}</small></td>
                  <td>{user.interest || "—"}</td>
                  <td>{formatDateTime(user.created_at)}</td>
                  <td>{formatDateTime(user.last_access_at)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <Pagination meta={pageMeta} onPage={(page) => fetchPage(page)} />
        </>
      ) : null}
      </section>
    </section>
  );
}

function UsersPanel({ users, load, pageMeta, onLoad }: {
  users: AdminUser[];
  load: PanelLoad;
  pageMeta: PageMeta;
  onLoad: (value: string, page: number) => void;
}) {
  return <RegisteredUsersView users={users} load={load} pageMeta={pageMeta} onLoad={onLoad} />;
}

function TrackingPanel({ tracking, load, platform, setPlatform, onReload }: {
  tracking: TrackingAnalytics | null;
  load: PanelLoad;
  platform: "" | TrackingPlatform;
  setPlatform: (value: "" | TrackingPlatform) => void;
  onReload: (value: "" | TrackingPlatform) => void;
}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"trials" | "clicks" | "conversion" | "newest">("trials");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [slugDraft, setSlugDraft] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!composerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setComposerOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [composerOpen]);

  const visible = useMemo(() => {
    if (!tracking) return [];
    const query = search.trim().toLowerCase();
    return tracking.items
      .filter((item) => !platform || item.platform === platform)
      .filter((item) => !query || [
        item.content_title,
        item.campaign_name,
        item.slug,
        item.platform,
      ].some((value) => String(value ?? "").toLowerCase().includes(query)))
      .sort((a, b) => {
        if (sort === "clicks") return Number(b.clicks) - Number(a.clicks);
        if (sort === "conversion") {
          const aRate = Number(a.trial_starts) / Math.max(1, Number(a.clicks));
          const bRate = Number(b.trial_starts) / Math.max(1, Number(b.clicks));
          return bRate - aRate;
        }
        if (sort === "newest") return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        return Number(b.trial_starts) - Number(a.trial_starts);
      });
  }, [platform, search, sort, tracking]);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setCreating(true);
    setStatus("Creating tracked link…");
    try {
      const result = await createTrackingLink({
        slug: String(data.get("slug") ?? ""),
        platform: String(data.get("platform") ?? "youtube") as TrackingPlatform,
        contentTitle: String(data.get("contentTitle") ?? ""),
        campaignName: String(data.get("campaignName") ?? "").trim() || null,
        videoUrl: String(data.get("videoUrl") ?? "").trim() || null,
        destination: String(data.get("destination") ?? "browser_trial") as TrackingDestination,
      });
      await navigator.clipboard.writeText(result.link.tracking_url).catch(() => undefined);
      setStatus("Link created and copied.");
      form.reset();
      setTitleDraft("");
      setSlugDraft("");
      setSlugTouched(false);
      setComposerOpen(false);
      onReload(platform);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "The tracking link could not be created.");
    } finally {
      setCreating(false);
    }
  }

  async function toggle(id: string, active: boolean) {
    setUpdatingId(id);
    setStatus(active ? "Activating link…" : "Pausing link…");
    try {
      await updateTrackingLink(id, { active });
      setStatus(active ? "Link activated." : "Link paused. Analytics were preserved.");
      onReload(platform);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "The link could not be updated.");
    } finally {
      setUpdatingId(null);
    }
  }

  if (!tracking && load.loading) return <LoadingState message="Loading campaign attribution…" />;
  if (!tracking && load.error) return <ErrorState message={load.error} onRetry={() => onReload(platform)} />;
  if (!tracking) return <Empty title="No campaign data" message="Campaign attribution will appear after creating a tracking link." />;

  const totals = tracking.totals;
  const maxDaily = Math.max(1, ...tracking.daily.map((row) => Number(row.clicks)));
  const channelLabel = platform ? `${platform.charAt(0).toUpperCase()}${platform.slice(1)}` : "All channels";

  return (
    <section className="tracking-layout">
      <div className="section-actions">
        <div>
          <span className="context-label">{channelLabel}</span>
          <p>Bot previews are excluded from click and conversion totals.</p>
        </div>
        <button className="primary-button" onClick={() => setComposerOpen(true)}>New tracking link</button>
      </div>

      {status ? <div className="action-status" role="status">{status}</div> : null}
      <PanelFeedback load={load} hasData={tracking.items.length > 0} onRetry={() => onReload(platform)} />

      <div className="tracking-kpis">
        <Stat label="Human clicks" value={number(totals.clicks)} hint={`${number(totals.unique_visitors)} unique visitors`} />
        <Stat label="Email requests" value={number(totals.optins)} hint={`${percent(Number(totals.optins), Number(totals.clicks))} of clicks`} />
        <Stat label="Verified emails" value={number(totals.verified_emails)} hint={`${percent(Number(totals.verified_emails), Number(totals.optins))} confirmed`} />
        <Stat label="Trial starts" value={number(totals.trial_starts)} hint={`${percent(Number(totals.trial_starts), Number(totals.clicks))} click to play`} />
      </div>

      <section className="panel tracking-table-panel">
        <div className="tracking-toolbar">
          <div className="platform-filter" role="group" aria-label="Filter campaign links by platform">
            {(["", "youtube", "tiktok", "instagram"] as const).map((item) => (
              <button
                type="button"
                key={item || "all"}
                className={platform === item ? "active" : ""}
                aria-pressed={platform === item}
                onClick={() => {
                  setPlatform(item);
                  onReload(item);
                }}
              >
                {item ? `${item.charAt(0).toUpperCase()}${item.slice(1)}` : "All"}
              </button>
            ))}
          </div>
          <label className="search">Search links
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Title, campaign, or link" />
          </label>
          <label className="compact-select">Sort
            <select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}>
              <option value="trials">Most trial starts</option>
              <option value="conversion">Best conversion</option>
              <option value="clicks">Most clicks</option>
              <option value="newest">Newest</option>
            </select>
          </label>
          <span className="result-count">{visible.length} shown</span>
        </div>

        {visible.length ? (
          <div className="table-scroll">
            <table className="data-table tracking-table">
              <thead><tr><th>Content</th><th>Tracking link</th><th>Clicks</th><th>Verified</th><th>Trials</th><th>Conversion</th><th>Status</th></tr></thead>
              <tbody>{visible.map((link) => (
                <tr key={link.id} className={link.active ? "" : "muted-row"}>
                  <td>
                    <span className={`platform-badge ${link.platform}`}>{link.platform}</span>
                    <strong>{link.content_title}</strong>
                    <small>{link.campaign_name || new Date(link.created_at).toLocaleDateString()}</small>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="copy-link"
                      onClick={() => {
                        void navigator.clipboard.writeText(link.tracking_url).then(
                          () => setStatus("Tracking link copied."),
                          () => setStatus(link.tracking_url),
                        );
                      }}
                    >
                      <span>Copy</span>{link.tracking_url.replace(/^https?:\/\//, "")}
                    </button>
                    {link.video_url ? <a className="video-link" href={link.video_url} target="_blank" rel="noreferrer">Open published video</a> : null}
                  </td>
                  <td><strong>{number(link.clicks)}</strong><small>{number(link.unique_visitors)} unique · {number(link.preview_bots)} previews excluded</small></td>
                  <td>{number(link.verified_emails)}</td>
                  <td><strong className="trial-value">{number(link.trial_starts)}</strong></td>
                  <td>{percent(Number(link.trial_starts), Number(link.clicks))}</td>
                  <td>
                    <button
                      type="button"
                      disabled={updatingId === link.id}
                      className={`status-toggle ${link.active ? "active" : ""}`}
                      onClick={() => void toggle(link.id, !link.active)}
                    >
                      {updatingId === link.id ? "Updating" : link.active ? "Active" : "Paused"}
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : (
          <Empty title="No matching links" message="Adjust the platform, search, or create a new tracking link." />
        )}
      </section>

      <section className="panel tracking-trend">
        <PanelTitle title="14-day attribution" meta={channelLabel} />
        {tracking.daily.length ? (
          <>
            <div className="mini-bars" aria-hidden="true">
              {tracking.daily.map((row) => (
                <div className="mini-bar-day" key={row.day} title={`${row.day}: ${row.clicks} clicks, ${row.verified_emails} verified emails, ${row.trial_starts} trials`}>
                  <div className="mini-bar-group">
                    <span className="clicks" style={{ height: `${Math.max(6, (Number(row.clicks) / maxDaily) * 100)}%` }} />
                    <span className="verified" style={{ height: `${Math.max(4, (Number(row.verified_emails) / maxDaily) * 100)}%` }} />
                    <span className="trials" style={{ height: `${Math.max(4, (Number(row.trial_starts) / maxDaily) * 100)}%` }} />
                  </div>
                  <small>{new Date(`${row.day}T12:00:00`).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}</small>
                </div>
              ))}
            </div>
            <ol className="sr-only">
              {tracking.daily.map((row) => <li key={row.day}>{row.day}: {row.clicks} clicks, {row.verified_emails} verified emails, {row.trial_starts} trial starts</li>)}
            </ol>
          </>
        ) : <Empty compact title="No attributed activity yet" message="Clicks, verified emails, and trial starts will appear here." />}
        <div className="funnel-key">
          <span><i className="clicks" /> Clicks</span>
          <span><i className="verified" /> Verified emails</span>
          <span><i className="trials" /> Trial starts</span>
        </div>
      </section>

      {composerOpen ? (
        <div className="drawer-layer">
          <button className="drawer-scrim" aria-label="Close tracking link form" onClick={() => setComposerOpen(false)} />
          <section className="form-drawer" role="dialog" aria-modal="true" aria-labelledby="tracking-drawer-title">
            <header className="drawer-header">
              <div>
                <span className="context-label">Campaign links</span>
                <h2 id="tracking-drawer-title">Create tracking link</h2>
                <p>Use one link per published video so attribution stays specific.</p>
              </div>
              <button className="icon-button" aria-label="Close" onClick={() => setComposerOpen(false)}>×</button>
            </header>
            <form className="tracking-create" onSubmit={create}>
              <div className="form-grid">
                <label>Platform
                  <select name="platform" defaultValue="youtube">
                    <option value="youtube">YouTube</option>
                    <option value="tiktok">TikTok</option>
                    <option value="instagram">Instagram</option>
                  </select>
                </label>
                <label>Video title
                  <input
                    name="contentTitle"
                    value={titleDraft}
                    onChange={(event) => {
                      const value = event.target.value;
                      setTitleDraft(value);
                      if (!slugTouched) setSlugDraft(slugify(value));
                    }}
                    placeholder="I survived Area 5 with one health"
                    required
                    maxLength={160}
                  />
                </label>
                <label>Tracking name
                  <div className="slug-input"><span>/go/</span><input
                    name="slug"
                    value={slugDraft}
                    onChange={(event) => {
                      setSlugTouched(true);
                      setSlugDraft(slugify(event.target.value));
                    }}
                    placeholder="area-5-one-health"
                    minLength={3}
                    maxLength={64}
                    pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                    required
                  /></div>
                </label>
                <label>Campaign <small>Optional</small>
                  <input name="campaignName" placeholder="Map 1 launch week" maxLength={120} />
                </label>
                <label>Published video URL <small>Optional</small>
                  <input name="videoUrl" type="url" placeholder="https://youtube.com/…" />
                </label>
                <label>Send clicks to
                  <select name="destination" defaultValue="browser_trial">
                    <option value="browser_trial">Browser trial gate</option>
                    <option value="marketing">Website field-pass section</option>
                  </select>
                </label>
              </div>
              <div className="drawer-actions">
                <button type="button" className="secondary-button" onClick={() => setComposerOpen(false)}>Cancel</button>
                <button className="primary-button" disabled={creating}>{creating ? "Creating…" : "Create and copy link"}</button>
              </div>
              <span className="form-status" role="status">{creating ? status : ""}</span>
            </form>
          </section>
        </div>
      ) : null}
    </section>
  );
}

function SoundsPanel({ sounds, load, onSaved }: {
  sounds: SoundSetting[];
  load: PanelLoad;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState<string | null>(null);
  const [status, setStatus] = useState("");

  async function save(sound: SoundSetting, volume: string, enabled: boolean) {
    setSaving(sound.cue_key);
    setStatus(`Saving ${sound.display_name}…`);
    try {
      await updateSound(sound.cue_key, {
        volumeBasisPoints: Math.round(Number(volume)),
        enabled,
      });
      setStatus(`${sound.display_name} saved.`);
      onSaved();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "The sound setting could not be saved.");
    } finally {
      setSaving(null);
    }
  }

  if (load.loading && !sounds.length) return <LoadingState message="Loading game settings…" />;
  if (load.error && !sounds.length) return <ErrorState message={load.error} onRetry={onSaved} />;

  return (
    <section className="panel settings-panel">
      <PanelTitle title="Sound controls" meta={`${sounds.length} cues`} />
      {status ? <div className="action-status" role="status">{status}</div> : null}
      <PanelFeedback load={load} hasData={sounds.length > 0} onRetry={onSaved} />
      {!sounds.length ? <Empty title="No sound cues" message="Live sound settings will appear when cues are configured." /> : null}
      <div className="sound-list">
        {sounds.map((sound) => (
          <form key={sound.cue_key} className="sound-row" onSubmit={(event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            void save(sound, String(data.get("volume") ?? "1"), data.get("enabled") === "on");
          }}>
            <div className="sound-identity">
              <strong>{sound.display_name}</strong>
              <small>{sound.category} · {sound.cue_key}</small>
              <audio controls preload="none" src={soundUrl(sound.asset_url)} />
            </div>
            <label>Volume %
              <input name="volume" type="number" min="0" max="200" step="5" defaultValue={sound.volume_basis_points} />
            </label>
            <label className="check"><input name="enabled" type="checkbox" defaultChecked={sound.enabled} /> Enabled</label>
            <button className="secondary-button" disabled={saving === sound.cue_key}>{saving === sound.cue_key ? "Saving…" : "Save"}</button>
          </form>
        ))}
      </div>
    </section>
  );
}

function AnalyticsPanel({ analytics, load, onRetry }: {
  analytics: Analytics | null;
  load: PanelLoad;
  onRetry: () => void;
}) {
  if (!analytics && load.loading) return <LoadingState message="Loading analytics…" />;
  if (!analytics && load.error) return <ErrorState message={load.error} onRetry={onRetry} />;
  if (!analytics) return <Empty title="No analytics data" message="Activity will appear after gameplay events are recorded." />;

  const groups = [
    ["Events", analytics.events, "event_type", "Last 7 days"],
    ["Areas", analytics.areas, "area_name", "Last 7 days"],
    ["Client versions", analytics.versions, "client_version", "Last 30 days"],
    ["Platforms", analytics.platforms, "platform", "Last 30 days"],
  ] as const;

  return (
    <>
      <PanelFeedback load={load} hasData onRetry={onRetry} />
      <section className="analytics-grid">
        {groups.map(([title, rows, key, range]) => (
          <section className="panel analytics-panel" key={title}>
            <PanelTitle title={title} meta={range} />
            {rows.length ? (
              <div className="table-scroll">
                <table className="data-table compact-table">
                  <thead><tr><th>Dimension</th><th>Activity</th></tr></thead>
                  <tbody>{rows.map((row, index) => (
                    <tr key={`${title}-${index}`}><td><strong>{String(row[key] ?? "Unknown")}</strong></td><td>{number(row.count)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            ) : <Empty compact title={`No ${title.toLowerCase()}`} message={`No activity was recorded in the ${range.toLowerCase()}.`} />}
          </section>
        ))}
      </section>
    </>
  );
}

function LeadersPanel({ rows, metric, scopeKey, setMetric, setScopeKey, load, reload }: {
  rows: LeaderboardRow[];
  metric: string;
  scopeKey: string;
  setMetric: (value: string) => void;
  setScopeKey: (value: string) => void;
  load: PanelLoad;
  reload: () => void;
}) {
  return (
    <section className="panel table-panel">
      <form className="table-toolbar leaderboard-toolbar" onSubmit={(event) => {
        event.preventDefault();
        reload();
      }}>
        <label>Metric
          <select value={metric} onChange={(event) => setMetric(event.target.value)}>
            {Object.entries(metricLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="scope-input">Scope
          <input value={scopeKey} onChange={(event) => setScopeKey(event.target.value)} placeholder="global, map:1, area:5" />
        </label>
        <button className="primary-button" disabled={load.loading}>{load.loading ? "Loading…" : "Load rankings"}</button>
        <span className="result-count">{rows.length} shown</span>
      </form>
      {load.error && !rows.length ? <ErrorState message={load.error} onRetry={reload} /> : null}
      <PanelFeedback load={load} hasData={rows.length > 0} onRetry={reload} />
      {!load.loading && !load.error && !rows.length ? <Empty title="No rankings found" message="Check the metric and use a scope such as global, map:1, or area:5." /> : null}
      {rows.length ? (
        <div className="table-scroll">
          <table className="data-table leaderboard-table">
            <thead><tr><th>Rank</th><th>Player</th><th>Scope</th><th>Metric</th><th>Value</th><th>Email</th><th>Projected</th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={`${row.scope_key}-${row.metric}-${row.gamer_tag}`}>
                <td><strong>#{row.rank}</strong></td>
                <td><strong>{row.gamer_tag}</strong></td>
                <td>{row.scope_key}</td>
                <td>{metricLabels[row.metric] ?? row.metric}</td>
                <td><strong>{formatLeaderboardValue(row)}</strong></td>
                <td>{row.primary_email ?? "Private"}</td>
                <td>{new Date(row.projected_at).toLocaleString()}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function NotificationsPanel({ campaigns, delivery, load, onCreated }: {
  campaigns: NotificationCampaign[];
  delivery: PushDeliveryStatus | null;
  load: PanelLoad;
  onCreated: () => void;
}) {
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setSubmitting(true);
    setStatus("Saving campaign…");
    try {
      await createNotification({
        title: String(data.get("title") ?? ""),
        body: String(data.get("body") ?? ""),
        target: String(data.get("target") ?? "all"),
        status: String(data.get("status") ?? "draft") as "draft" | "queued",
      });
      form.reset();
      setStatus("Campaign saved.");
      onCreated();
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "The campaign could not be saved.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="push-layout">
      <form className="panel notify-form" onSubmit={submit}>
        <PanelTitle title="Compose push" meta="Draft or queue" />
        <div className="inline-status" role="status">
          Delivery: {delivery?.provider === "native"
            ? `Live · ${delivery.availablePlatforms.join(" + ") || "no platform credentials"}`
            : delivery?.provider === "log"
              ? "Test logging only"
              : "Provider credentials required"}
        </div>
        <label>Title<input name="title" required maxLength={100} /></label>
        <label>Message<textarea name="body" required maxLength={500} /></label>
        <label>Audience
          <select name="target">
            <option value="all">All enabled devices</option>
            <option value="ios">iPhone players</option>
            <option value="android">Android players</option>
            <option value="inactive_24h">Inactive 24h</option>
            <option value="map2_players">Map 2 players</option>
          </select>
        </label>
        <label>Status
          <select name="status">
            <option value="draft">Save draft</option>
            <option value="queued">Queue campaign</option>
          </select>
        </label>
        <button className="primary-button" disabled={submitting}>{submitting ? "Saving…" : "Save campaign"}</button>
        <span className="form-status" role="status">{status}</span>
      </form>

      <section className="panel campaign-history">
        <PanelTitle title="Campaign history" meta={`${campaigns.length} records`} />
        {load.error && !campaigns.length ? <ErrorState message={load.error} onRetry={onCreated} /> : null}
        {load.loading && !campaigns.length ? <LoadingState message="Loading push campaigns…" /> : null}
        <PanelFeedback load={load} hasData={campaigns.length > 0} onRetry={onCreated} />
        {!load.loading && !load.error && !campaigns.length ? <Empty title="No push campaigns" message="Saved drafts and queued campaigns will appear here." /> : null}
        {campaigns.length ? (
          <div className="table-scroll">
            <table className="data-table campaigns-table">
              <thead><tr><th>Campaign</th><th>Target</th><th>Status</th><th>Audience</th><th>Accepted</th><th>Opened</th><th>Failed</th><th>Created</th></tr></thead>
              <tbody>{campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td><strong>{campaign.title}</strong><small>{campaign.body}</small></td>
                  <td>{campaign.target}</td>
                  <td><span className={`pill ${campaign.status === "draft" ? "neutral" : "good"}`}>{campaign.status}</span></td>
                  <td>{number(campaign.audience.total)}</td>
                  <td>{number(campaign.metrics.accepted)}</td>
                  <td>{number(campaign.metrics.opened)}</td>
                  <td>{number(campaign.metrics.failed)}</td>
                  <td>{new Date(campaign.created_at).toLocaleString()}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        ) : null}
      </section>
    </section>
  );
}

export function App() {
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [authMessage, setAuthMessage] = useState("");
  const [tab, setTab] = useState<Tab>("overview");
  const [navOpen, setNavOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [panelLoads, setPanelLoads] = useState<Record<Tab, PanelLoad>>(initialPanelLoads);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersPage, setUsersPage] = useState<PageMeta>({ total: 0, page: 1, pageSize: 50, totalPages: 1 });
  const [trialUsers, setTrialUsers] = useState<TrialUser[]>([]);
  const [trialLoad, setTrialLoad] = useState<PanelLoad>({ loading: false, error: "" });
  const [trialPage, setTrialPage] = useState<PageMeta>({ total: 0, page: 1, pageSize: 25, totalPages: 1 });
  const [contactSummary, setContactSummary] = useState<ContactSummary>({
    total: 0,
    launch_contacts: 0,
    browser_trial_requests: 0,
    verified_emails: 0,
    delivered_emails: 0,
    opened_emails: 0,
    delivery_failures: 0,
    trial_starts: 0,
    new_contacts_7d: 0,
    unsubscribed: 0,
  });
  const [trialRequested, setTrialRequested] = useState(false);
  const [trialFilters, setTrialFilters] = useState<ContactLoadInput>({
    search: "",
    page: 1,
    status: "",
    source: "",
    segment: "",
    sort: "newest",
  });
  const [sounds, setSounds] = useState<SoundSetting[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [tracking, setTracking] = useState<TrackingAnalytics | null>(null);
  const [trackingPlatform, setTrackingPlatform] = useState<"" | TrackingPlatform>("");
  const [campaigns, setCampaigns] = useState<NotificationCampaign[]>([]);
  const [pushDelivery, setPushDelivery] = useState<PushDeliveryStatus | null>(null);
  const [leaderRows, setLeaderRows] = useState<LeaderboardRow[]>([]);
  const [metric, setMetric] = useState("xp");
  const [scopeKey, setScopeKey] = useState("global");

  const runLoad = useCallback(async function runLoad<T>(
    key: Tab,
    request: () => Promise<T>,
    apply: (value: T) => void,
  ) {
    setPanelLoads((current) => ({ ...current, [key]: { loading: true, error: "" } }));
    try {
      const value = await request();
      apply(value);
      setPanelLoads((current) => ({ ...current, [key]: { loading: false, error: "" } }));
      return true;
    } catch (error) {
      if (error instanceof AdminApiError && (error.status === 401 || error.status === 403)) throw error;
      const message = error instanceof Error ? error.message : "Admin data is unavailable.";
      setPanelLoads((current) => ({ ...current, [key]: { loading: false, error: message } }));
      return false;
    }
  }, []);

  const loadOverview = useCallback(
    () => runLoad("overview", getOverview, setOverview),
    [runLoad],
  );
  const loadUsers = useCallback(
    (search = "", page = 1) => runLoad("users", () => getUsers(search, page, 50), (data) => {
      setUsers(data.items);
      setUsersPage({
        total: Number(data.total ?? data.items.length),
        page: Number(data.page ?? page),
        pageSize: Number(data.pageSize ?? 50),
        totalPages: Number(data.totalPages ?? 1),
      });
    }),
    [runLoad],
  );
  const loadTrialLeads = useCallback(async (input: ContactLoadInput) => {
    setTrialRequested(true);
    setTrialFilters(input);
    setTrialLoad({ loading: true, error: "" });
    try {
      const data = await getTrialUsers({ ...input, pageSize: 25 });
      setTrialUsers(data.items);
      setTrialPage({
        total: Number(data.total ?? data.items.length),
        page: Number(data.page ?? input.page),
        pageSize: Number(data.pageSize ?? 25),
        totalPages: Number(data.totalPages ?? 1),
      });
      setContactSummary({
        total: Number(data.summary?.total ?? 0),
        launch_contacts: Number(data.summary?.launch_contacts ?? 0),
        browser_trial_requests: Number(data.summary?.browser_trial_requests ?? 0),
        verified_emails: Number(data.summary?.verified_emails ?? 0),
        delivered_emails: Number(data.summary?.delivered_emails ?? 0),
        opened_emails: Number(data.summary?.opened_emails ?? 0),
        delivery_failures: Number(data.summary?.delivery_failures ?? 0),
        trial_starts: Number(data.summary?.trial_starts ?? 0),
        new_contacts_7d: Number(data.summary?.new_contacts_7d ?? 0),
        unsubscribed: Number(data.summary?.unsubscribed ?? 0),
      });
      setTrialLoad({ loading: false, error: "" });
    } catch (error) {
      if (error instanceof AdminApiError && error.status === 401) {
        setAuthState("login");
        return;
      }
      if (error instanceof AdminApiError && error.status === 403) {
        setAuthMessage(error.message);
        setAuthState("forbidden");
        return;
      }
      setTrialLoad({
        loading: false,
        error: error instanceof Error ? error.message : "Browser trial leads are unavailable.",
      });
    }
  }, []);
  const loadSounds = useCallback(
    () => runLoad("sounds", getSounds, (data) => setSounds(data.items)),
    [runLoad],
  );
  const loadAnalytics = useCallback(
    () => runLoad("analytics", getAnalytics, setAnalytics),
    [runLoad],
  );
  const loadTracking = useCallback(
    (platform: "" | TrackingPlatform = "") => runLoad("tracking", () => getTrackingLinks(platform), setTracking),
    [runLoad],
  );
  const loadNotifications = useCallback(
    () => runLoad("notifications", getNotifications, (data) => {
      setCampaigns(data.items);
      setPushDelivery(data.delivery);
    }),
    [runLoad],
  );
  const loadLeaders = useCallback(
    (nextMetric: string, nextScope: string) => runLoad("leaders", () => getLeaderboard(nextMetric, nextScope), (data) => setLeaderRows(data.items)),
    [runLoad],
  );

  useEffect(() => {
    let current = true;
    const boot = async () => {
      try {
        await Promise.all([
          loadOverview(),
          loadUsers(),
          loadTrialLeads({
            search: "",
            page: 1,
            status: "",
            source: "",
            segment: "",
            sort: "newest",
          }),
          loadTracking(""),
          loadAnalytics(),
          loadLeaders("xp", "global"),
          loadNotifications(),
          loadSounds(),
        ]);
        if (!current) return;
        setLastRefreshed(new Date());
        setAuthState("signed-in");
      } catch (error) {
        if (!current) return;
        if (error instanceof AdminApiError && error.status === 401) {
          setAuthState("login");
          return;
        }
        if (error instanceof AdminApiError && error.status === 403) {
          setAuthMessage(error.message);
          setAuthState("forbidden");
          return;
        }
        setAuthState("signed-in");
      }
    };
    void boot();
    return () => {
      current = false;
    };
  }, [loadAnalytics, loadLeaders, loadNotifications, loadOverview, loadSounds, loadTracking, loadTrialLeads, loadUsers]);

  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navOpen]);

  async function refreshAll() {
    setRefreshing(true);
    try {
      await Promise.all([
        loadOverview(),
        loadUsers(),
        trialRequested ? loadTrialLeads(trialFilters) : Promise.resolve(),
        loadTracking(trackingPlatform),
        loadAnalytics(),
        loadLeaders(metric, scopeKey),
        loadNotifications(),
        loadSounds(),
      ]);
      setLastRefreshed(new Date());
    } catch (error) {
      if (error instanceof AdminApiError && error.status === 401) setAuthState("login");
      if (error instanceof AdminApiError && error.status === 403) {
        setAuthMessage(error.message);
        setAuthState("forbidden");
      }
    } finally {
      setRefreshing(false);
    }
  }

  if (authState === "checking") {
    return (
      <main className="login-main">
        <section className="login-card compact">
          <div className="login-brand"><div className="brand-mark">AR</div><div><strong>Afterrain</strong><span>Admin</span></div></div>
          <p className="kicker">Owner access</p>
          <h1>Checking session</h1>
          <p className="login-copy">Verifying owner permissions and loading admin data.</p>
          <LoadingState message="Checking owner session…" />
        </section>
      </main>
    );
  }

  if (authState === "login" || authState === "forbidden") {
    return <LoginScreen mode={authState} message={authState === "forbidden" ? authMessage : undefined} />;
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <button
        className={`sidebar-scrim ${navOpen ? "visible" : ""}`}
        aria-label="Close navigation"
        tabIndex={navOpen ? 0 : -1}
        onClick={() => setNavOpen(false)}
      />
      <aside className={`app-sidebar ${navOpen ? "open" : ""}`} aria-label="Admin navigation">
        <div className="sidebar-header">
          <div className="brand">
            <span className="brand-mark">AR</span>
            <span className="brand-copy"><strong>Afterrain</strong><small>Admin</small></span>
          </div>
          <button className="icon-button sidebar-close" aria-label="Close navigation" onClick={() => setNavOpen(false)}>×</button>
        </div>
        <nav>
          {navigation.map((group) => (
            <div className="nav-group" key={group.label}>
              <span className="nav-label">{group.label}</span>
              {group.items.map(([key, label]) => (
                <button
                  key={key}
                  className={tab === key ? "active" : ""}
                  aria-current={tab === key ? "page" : undefined}
                  onClick={() => {
                    setTab(key);
                    setNavOpen(false);
                  }}
                >
                  <Icon name={tabMeta[key].icon} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <a href="https://play.theafterrain.com" target="_blank" rel="noreferrer">Open live game <span aria-hidden="true">↗</span></a>
          <small>Owner-only workspace</small>
        </div>
      </aside>

      <main className="workspace" id="main-content">
        <header className="page-header">
          <div className="page-heading">
            <button
              className="icon-button nav-toggle"
              aria-label="Open navigation"
              aria-expanded={navOpen}
              onClick={() => setNavOpen(true)}
            >
              <span /><span /><span />
            </button>
            <div>
              <span className="context-label">{tabMeta[tab].eyebrow}</span>
              <h1>{tabMeta[tab].title}</h1>
              <p>{tabMeta[tab].copy}</p>
            </div>
          </div>
          <div className="header-actions">
            <div className="freshness">
              <span className="live-dot" />
              <span>{lastRefreshed ? `Updated ${lastRefreshed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Live API"}</span>
            </div>
            <button className="secondary-button refresh-button" disabled={refreshing} onClick={() => void refreshAll()}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </header>

        <div className="workspace-content">
          {tab === "overview" ? <OverviewPanel overview={overview} load={panelLoads.overview} onRetry={() => void loadOverview()} /> : null}
          {tab === "users" ? (
            <UsersPanel
              users={users}
              load={panelLoads.users}
              pageMeta={usersPage}
              onLoad={(value, page) => void loadUsers(value, page)}
            />
          ) : null}
          {tab === "contacts" ? (
            <ContactsPanel
              users={trialUsers}
              load={trialLoad}
              pageMeta={trialPage}
              summary={contactSummary}
              onLoad={(input) => void loadTrialLeads(input)}
            />
          ) : null}
          {tab === "tracking" ? (
            <TrackingPanel
              tracking={tracking}
              load={panelLoads.tracking}
              platform={trackingPlatform}
              setPlatform={setTrackingPlatform}
              onReload={(value) => void loadTracking(value)}
            />
          ) : null}
          {tab === "analytics" ? <AnalyticsPanel analytics={analytics} load={panelLoads.analytics} onRetry={() => void loadAnalytics()} /> : null}
          {tab === "leaders" ? (
            <LeadersPanel
              rows={leaderRows}
              metric={metric}
              scopeKey={scopeKey}
              setMetric={setMetric}
              setScopeKey={setScopeKey}
              load={panelLoads.leaders}
              reload={() => void loadLeaders(metric, scopeKey)}
            />
          ) : null}
          {tab === "notifications" ? <NotificationsPanel campaigns={campaigns} delivery={pushDelivery} load={panelLoads.notifications} onCreated={() => void loadNotifications()} /> : null}
          {tab === "sounds" ? <SoundsPanel sounds={sounds} load={panelLoads.sounds} onSaved={() => void loadSounds()} /> : null}
        </div>
      </main>
    </div>
  );
}
